import React, { useContext, useRef, useState } from "react";

import get from "lodash/get";
import head from "lodash/head";
import { itunesCategoriesByUUID } from "../../../lib/itunes_categories";
import { Col, Overlay, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { DefaultShowSearchImage } from "../show_search/show_search";
import { formatMoney } from "../../../lib/format-money";
import {
  getAverageCPMFromShow,
  getEstimatedImpressionsForShow,
  getDurationInDays,
} from "../../../lib/campaigns";
import Moment from "moment";
import { Link } from "react-router-dom";
import { CalendarOverlay, useNumberOverlay } from "./overlays";
import { hummanizeAudienceSize } from "../../../lib/numbers";
import Magnifier from "../../../icons/Magnifier.svg";
import MagnifierHover from "../../../icons/Magnifier_Hovered.svg";
import MagnifierPressed from "../../../icons/Magnifier_Pressed.svg";
import ExclusiveTag from "../exclusive_tag";
import { itunesCategoryUUIDToName } from "../../../data/itunes_categories";
import Divider from "../divider";
import { CampaignStyleHostRead, CampaignStyleToDisplay } from "../../../constants/campaigns";
import { Provider, useDispatch, useSelector, useStore } from "react-redux";
import { getSampleAdReads } from "../../../action_managers/campaigns";
import MediaPlayer from "../media_player";

export const TitleContainer = ({ width, subTitle, campaign }) => {
  const show = useContext(ShowContext);
  return (
    <Col xs={width}>
      <TitleContainerCell show={show} subTitle={subTitle} campaign={campaign} />
    </Col>
  );
};

export const TitleContainerCell = ({ show, subTitle, campaign }) => {
  const categoryName = get(itunesCategoriesByUUID, [head(show.categoryUUIDs), "name"], "");
  subTitle = subTitle ?? `Category: ${categoryName}`;
  return (
    <div className={"black-text"}>
      <Link
        className={"no-style-link title-container "}
        target="_blank"
        rel="noopener noreferrer"
        to={`/browse/${show.uuid}`}>
        <div className={"bold title flex-row-container "}>
          <div className={"ellipsis"} style={{ maxWidth: 200, display: "block" }}>
            {show.title}
          </div>
          {campaign && <MagnifierToolTip show={show} campaign={campaign} />}
        </div>
        <div className={"category small"}>{subTitle}</div>
      </Link>
    </div>
  );
};

export const MagnifierToolTip = ({ show, campaign }) => {
  const [pressed, setPressed] = useState(false);
  const [hover, setHover] = useState(false);
  const dispatch = useDispatch();
  const adReads = useSelector(state => state.publicShows.adReadsByShowUUID?.[show.uuid]?.[0] ?? {});

  let image = Magnifier;
  if (hover && !pressed) {
    image = MagnifierHover;
  }
  if (pressed) {
    image = MagnifierPressed;
  }
  // this looks shady, but because this is taking place inside of a tooltip, which is not in the
  // normal react dom tree, we have to ensure that the connect function underneath has the
  // store provided through context
  const store = useStore();
  const mediaPlayer = adReads.mediaFileUUID ? (
    <Provider store={store}>
      <MediaPlayer mediaFileUUID={adReads.mediaFileUUID} mini={true} />
    </Provider>
  ) : null;

  const overlay = () => {
    let adDetails = Object.entries(campaign.targetingOptions)
      .filter(([, enabled]) => enabled)
      .map(([key]) => {
        const position = key.slice(0, -4);
        return position[0].toUpperCase() + position.slice(1) + "-Roll";
      })
      .join(", ");
    adDetails = CampaignStyleToDisplay[campaign.style] + " " + adDetails;
    if (campaign.style === CampaignStyleHostRead) {
      adDetails = "0:60 " + adDetails;
    }
    return (
      <Popover id={"show-popover"} bsClass={"p-axxs popover"} style={{ width: 248 }}>
        <div>
          <div className={"bold m-bxxxs"}>{show.title}</div>
          <ExclusiveTag>{itunesCategoryUUIDToName(show.categoryUUIDs[0])}</ExclusiveTag>
          <Divider marginTop={16} marginBottom={16} />
          <div style={{ fontSize: 11 }}>
            <div className="flex-row-container align-center m-bxxxs">
              <span className="bold m-rxxxs">Hosts: </span>
              <div>
                {(show.advertisementSettings?.hosts ?? []).map((host, i) => {
                  return (
                    <div key={i}>
                      {host.fullName?.firstName} {host.fullName?.lastName}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={"m-bxxxs"}>
              <span className="bold">Episode Frequency: </span>
              {show.estimatedEpisodeFrequency}
            </div>
            <div className={"m-bxxxs"}>
              <span className="bold">Ad Details: </span>
              {adDetails}
            </div>
            {adReads.mediaFileUUID && (
              <div className={"flex-row-container align-center"}>
                <div className="bold m-rxxxs">Past Host-Read Example Read: </div> {mediaPlayer}
              </div>
            )}
          </div>
        </div>
      </Popover>
    );
  };
  return (
    <OverlayTrigger
      trigger={["click"]}
      placement={"right"}
      overlay={overlay()}
      onExit={() => {
        setPressed(false);
      }}
      onEnter={() => {
        setPressed(true);
      }}
      rootClose>
      <img
        src={image}
        alt=""
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          dispatch(getSampleAdReads(show.uuid));
        }}
      />
    </OverlayTrigger>
  );
};

export const Title = ({ width }) => {
  const show = useContext(ShowContext);
  return (
    <Col xs={width} className={"title-container c-black"}>
      <div className={"bold title"}>{show.title}</div>
    </Col>
  );
};

export const Image = ({ width }) => {
  const show = useContext(ShowContext);
  return (
    <Col xs={width} xsHidden={true}>
      <Link target="_blank" rel="noopener noreferrer" to={`/browse/${show.uuid}`}>
        {DefaultShowSearchImage(show)}
      </Link>
    </Col>
  );
};

export const AudienceSize = ({ width }) => {
  const show = useContext(ShowContext);
  return (
    <Col xs={width}>
      <div className={"bold"}>
        {hummanizeAudienceSize(get(show, "estimatedWeeklyDownloads", 0))}
      </div>
    </Col>
  );
};

export const EpisodeSize = ({ width }) => {
  const show = useContext(ShowContext);
  return (
    <Col xs={width}>
      <div className={"bold"}>{hummanizeAudienceSize(get(show, "averageEpisodeDownloads", 0))}</div>
    </Col>
  );
};

export const SpotRate = ({ width, campaign }) => {
  const show = useContext(ShowContext);
  let path = "midRollSpotRate";
  Object.entries(campaign.targetingOptions).map(([position, shouldTarget]) => {
    if (shouldTarget) {
      path = position + "SpotRate";
    }
  });
  return (
    <Col xs={width}>
      <div className={"text-right bold"}>
        {formatMoney(get(show, ["advertisementSettings", path], 0))}
      </div>
    </Col>
  );
};

export const AverageCPM = ({ width, campaign }) => {
  const show = useContext(ShowContext);
  if (!campaign) return null;
  const campaignStyle = campaign.style;
  const prefix = campaignStyle === "hostRead" ? "hostRead" : "preRecorded";

  const overlay = () => (
    <Popover id={"CPM-popover"} bsClass={"p-a0 popover"}>
      <div className={"cpm-popover flex-row-container"}>
        {["PreRollCPM", "MidRollCPM", "PostRollCPM"].map(fieldName => (
          <div key={fieldName}>
            <div className={"bold small bg-mblue p-bxxs p-txs p-hxs line-height-1em"}>
              {fieldName.slice(0, -3).toUpperCase()}
            </div>
            <div className={"bold text-center p-hxxs p-txxs p-bxs  line-height-1em"}>
              {formatMoney(show?.advertisementSettings?.[prefix + fieldName] ?? 0)}
            </div>
          </div>
        ))}
      </div>
    </Popover>
  );
  return (
    <Col xs={width}>
      <OverlayTrigger trigger={["hover", "focus"]} placement={"top"} overlay={overlay()}>
        <div className={"bold"}>{formatMoney(getAverageCPMFromShow(show, campaign))}</div>
      </OverlayTrigger>
    </Col>
  );
};

export const AddToCampaign = ({ width, onRemove, onClick, selectedShows, disabledShows }) => {
  const show = useContext(ShowContext);

  if (disabledShows.has(show.uuid)) {
    return <span className="bold">Previously Selected</span>;
  }
  const selectState = selectedShows[show.uuid] ?? 0b00;

  return (
    <Col xs={width}>
      {selectState & 0b1 ? (
        <div className="fake-link text-right" onClick={() => onRemove(show)}>
          Remove
        </div>
      ) : (
        <div className="fake-link text-right" onClick={() => onClick(show)}>
          Invite
        </div>
      )}
    </Col>
  );
};

export const useTriggerIf = (conditions, Content, Triggercontent) => {
  const [showPopover, updateShowPopover] = useState(false);
  const close = () => updateShowPopover(false);
  const ref = useRef(null);
  return (
    <div>
      {Content({ ref, onClick: () => updateShowPopover(true) })}
      {conditions && (
        <Overlay
          rootClose
          placement={"bottom"}
          show={showPopover}
          onHide={() => updateShowPopover(false)}
          container={ref.current}
          target={() => ref.current}>
          {typeof Triggercontent === "function" ? Triggercontent(close) : Triggercontent}
        </Overlay>
      )}
    </div>
  );
};

export const Impressions = ({ width, onChange, campaign }) => {
  const show = useContext(ShowContext);
  const budget = useContext(BudgetContext);
  const impressions = getEstimatedImpressionsForShow(campaign, show, budget);
  const Content = ({ onClick, ref }) => (
    <div className={"impressions pointer"} onClick={onClick} ref={ref}>
      <span className="bold margin-right--small" {...{ ...(onChange && { onChange }) }}>
        Downloads:
      </span>
      &nbsp; {getEstimatedImpressionsForShow(campaign, show, budget).toLocaleString()}
    </div>
  );
  return (
    <Col xs={width}>
      {useTriggerIf(
        !!onChange,
        Content,
        useNumberOverlay({
          label: "IMPRESSIONS",
          confirmText: "Update Downloads",
          onChange,
          startValue: impressions,
        })
      )}
    </Col>
  );
};

export const EstimatedRunTime = ({ campaign, width, startDate, onChange }) => {
  const show = useContext(ShowContext);
  const budget = useContext(BudgetContext);
  const durationInDays = getDurationInDays(campaign, show, budget);
  const formatString = "MM/DD/YYYY";

  const endDate = Moment(startDate).add(durationInDays, "days");
  const content = ({ ref, onClick }) => (
    <div className={"pointer"} onClick={onClick}>
      <div className={"bold"}>Estimated Run Time: </div>
      <div>
        <span ref={ref} className={"position-relative width-fit-content"}>
          {Moment(startDate).format(formatString)} - {endDate.format(formatString)}
        </span>
      </div>
    </div>
  );
  return (
    <Col xs={width}>
      {useTriggerIf(!!onChange, content, CalendarOverlay(endDate, onChange, startDate))}
    </Col>
  );
};

export const Total = ({ width, onChange }) => {
  const budget = useContext(BudgetContext);
  const content = ({ onClick, ref }) => (
    <div className={"pointer"} onClick={onClick} ref={ref}>
      <div className={"bold"}>Total: </div>
      <div className={"width-fit-content position-relative"}>{formatMoney(budget)}</div>
    </div>
  );
  return (
    <Col xs={width}>
      {useTriggerIf(
        !!onChange,
        content,
        useNumberOverlay({
          label: "BUDGET",
          confirmText: "Update Budget",
          onChange: number => onChange(number * 100),
          startValue: budget / 100,
          prefix: "$",
        })
      )}
    </Col>
  );
};

const ShowContext = React.createContext();
const BudgetContext = React.createContext();
export const SearchRow = ({ children, show, budget }) => {
  return (
    <ShowContext.Provider value={show}>
      <BudgetContext.Provider value={budget}>
        <Row className={"campaign-show-table__search-result width-100"}>{children}</Row>
      </BudgetContext.Provider>
    </ShowContext.Provider>
  );
};

export const renderToContext = children => (text, show) => {
  return <ShowContext.Provider value={show}>{children}</ShowContext.Provider>;
};
